/**
 * ZIP File Processing Utilities
 * Uses JSZip for client-side ZIP extraction
 */

import JSZip from 'jszip';

/**
 * Process a ZIP file and extract PDF files
 * @param {File} zipFile - ZIP file to process
 * @param {Function} onProgress - Progress callback (current, total)
 * @returns {Promise<Array>} - Array of extracted file objects
 */
export const processZipFile = async (zipFile, onProgress = null) => {
  try {
    const zip = await JSZip.loadAsync(zipFile);
    const extractedFiles = [];
    
    // Get all files from ZIP
    const zipEntries = Object.values(zip.files);
    const pdfEntries = zipEntries.filter(entry => 
      !entry.dir && entry.name.toLowerCase().endsWith('.pdf')
    );
    
    // Extract each PDF file
    for (let i = 0; i < pdfEntries.length; i++) {
      const entry = pdfEntries[i];
      
      try {
        // Get file content as blob
        const blob = await entry.async('blob');
        
        // Create a File object from the blob
        const file = new File([blob], entry.name.split('/').pop(), {
          type: 'application/pdf'
        });
        
        extractedFiles.push({
          file,
          name: entry.name,
          size: entry._data.uncompressedSize,
          path: entry.name,
          source: 'zip'
        });
        
        if (onProgress) {
          onProgress(i + 1, pdfEntries.length);
        }
      } catch (error) {
        console.error(`Error extracting ${entry.name}:`, error);
        extractedFiles.push({
          name: entry.name,
          error: error.message,
          source: 'zip'
        });
      }
    }
    
    return extractedFiles;
  } catch (error) {
    console.error('Error processing ZIP file:', error);
    throw new Error('Failed to process ZIP file: ' + error.message);
  }
};

/**
 * Validate ZIP file
 * @param {File} file - File to validate
 * @returns {boolean} - True if valid ZIP
 */
export const isValidZipFile = (file) => {
  const validTypes = [
    'application/zip',
    'application/x-zip',
    'application/x-zip-compressed',
    'application/octet-stream'
  ];
  
  return validTypes.includes(file.type) || file.name.toLowerCase().endsWith('.zip');
};

/**
 * Get ZIP file info without extracting
 * @param {File} zipFile - ZIP file
 * @returns {Promise<Object>} - ZIP info
 */
export const getZipInfo = async (zipFile) => {
  try {
    const zip = await JSZip.loadAsync(zipFile);
    
    let pdfCount = 0;
    let totalSize = 0;
    
    zip.forEach((relativePath, entry) => {
      if (!entry.dir && entry.name.toLowerCase().endsWith('.pdf')) {
        pdfCount++;
        totalSize += entry._data.uncompressedSize;
      }
    });
    
    return {
      pdfCount,
      totalSize,
      fileName: zipFile.name
    };
  } catch (error) {
    console.error('Error reading ZIP info:', error);
    return { pdfCount: 0, totalSize: 0, fileName: zipFile.name, error: error.message };
  }
};

/**
 * Extract single file from ZIP
 * @param {File} zipFile - ZIP file
 * @param {string} filePath - Path to file inside ZIP
 * @returns {Promise<File>} - Extracted file
 */
export const extractSingleFile = async (zipFile, filePath) => {
  try {
    const zip = await JSZip.loadAsync(zipFile);
    const entry = zip.file(filePath);
    
    if (!entry) {
      throw new Error(`File ${filePath} not found in ZIP`);
    }
    
    const blob = await entry.async('blob');
    return new File([blob], entry.name.split('/').pop(), {
      type: 'application/pdf'
    });
  } catch (error) {
    console.error('Error extracting single file:', error);
    throw error;
  }
};
